/* global RequestInit */
import { getAuthToken } from './client';

export interface BookTop {
  bid: number | null;
  ask: number | null;
  bidSize: number;
  askSize: number;
  spread: number | null;
  updatedAt: number;
  fresh: boolean;
}

export type ResolvedFrom = 'market-slug' | 'event-slug' | 'soccer-3way' | 'kalshi-event';

/** Which team scored — the tap. 'A' is the first team on the fixture, 'B' the second. */
export type Side = 'A' | 'B';

/**
 * A market's key within the armed game: 'A' / 'draw' / 'B' for the moneyline, the Gamma slug for
 * every other market on the fixture.
 */
export type MarketKey = string;

export type OutcomeKey = 'yes' | 'no';

/** One tradeable token: a market's Yes or No. */
export type LegKey = `${MarketKey}:${OutcomeKey}`;

/** Where an order goes. Every leg exists on Polymarket; its Kalshi twin exists when a Kalshi event is armed. */
export type Venue = 'polymarket' | 'kalshi';

/** Expected goals per side, fitted at arm time from the fixture's three-way moneyline. */
export interface GoalModel {
  /** Team A. */
  armed: number;
  /** Team B. */
  opponent: number;
  error: number;
}

export type ClockPhase =
  | 'no-clock'
  | 'pre-match'
  | 'first-half'
  | 'half-time'
  | 'second-half'
  | 'in-play'
  | 'full-time';

/** The match clock, polled off the fixture's own Polymarket event. Never typed in by hand. */
export interface ClockInfo {
  /** The minute on the match clock, 0–90: it holds at 45 and at 90 through added time. Null when there is nothing to price a goal against. */
  minute: number | null;
  /** Added time already played in this half, in minutes. 0 until the clock reaches the half's end. */
  added?: number;
  phase: ClockPhase;
  /** The last poll is too old to trust; every leg is on the flat fallback until it recovers. */
  stale: boolean;
  live: boolean;
  ended: boolean;
  period: string | null;
  elapsed: number | null;
  /** The feed's score on our sides, or null until it reports one. */
  feedScore: Record<Side, number> | null;
  startTime: string | null;
  polledAt: string | null;
  /** Which feed the clock is reading — `livescore`, `polymarket` — or null when none covers the fixture. */
  source?: string | null;
  /** Real orders allowed before kick-off and after full time. */
  allowOutsideMatch?: boolean;
}

export const NO_CLOCK: ClockInfo = {
  minute: null,
  phase: 'no-clock',
  stale: false,
  live: false,
  ended: false,
  period: null,
  elapsed: null,
  feedScore: null,
  startTime: null,
  polledAt: null
};

/** The words `describeClock` uses before and after the game — soccer's by default, basketball's on the Courtside pages. */
export interface ClockWords {
  /** "kicks off" / "tips off". */
  kicksOff: string;
  /** "full time" / "end of the game". */
  fullTime: string;
}

const SOCCER_CLOCK_WORDS: ClockWords = { kicksOff: 'kicks off', fullTime: 'full time' };

/** `2H 58'`, `2H 90+3'`, `half-time`, `kicks off 13:30`… what the clock says, in a few characters. */
export function describeClock(clock: ClockInfo | undefined, words: ClockWords = SOCCER_CLOCK_WORDS): string {
  if (!clock || clock.phase === 'no-clock') {
    return 'no clock';
  }
  if (clock.stale) {
    return 'clock stale';
  }
  switch (clock.phase) {
    case 'pre-match':
      return clock.startTime
        ? `${words.kicksOff} ${new Date(clock.startTime).toLocaleTimeString([], { timeStyle: 'short' })}`
        : 'not started';
    case 'half-time':
      return 'half-time';
    case 'full-time':
      return words.fullTime;
    default: {
      // Floored like the minute itself, so the page reads what the feed shows: 90+3.
      const added = clock.added && clock.added >= 1 ? `+${Math.floor(clock.added)}` : '';
      const minute = clock.minute === null ? '?' : `${Math.floor(clock.minute)}${added}`;
      return `${clock.period ? `${clock.period} ` : ''}${minute}'`;
    }
  }
}

/** One leg a tap buys, on one venue, and why. */
export interface TapLeg {
  venue: Venue;
  leg: LegKey;
  ticker: string | null;
  label: string;
  why: string;
}

export interface MarketInfo<T> {
  key: MarketKey;
  /** 'moneyline', 'totals', 'spread', 'first_to_score', 'halftime_result', 'btts', 'team_totals'… */
  kind: string;
  /** Display group: "Moneyline", "Totals", "First to score"… */
  group: string;
  label: string;
  /** Gamma's outcome names in token order: ["Yes","No"], ["Over","Under"], [team, other team]. */
  outcomes: [string, string];
  /** The Polymarket listing, or null when only Kalshi lists this market. */
  polymarket: { conditionId: string; slug: string; question: string | null } | null;
  /** Null when Polymarket does not list this market. */
  legs: { yes: T; no: T } | null;
  /** The Kalshi listing, when a Kalshi event is armed and Kalshi lists this market. */
  kalshi: { ticker: string; title: string; legs: { yes: T; no: T } } | null;
}

/** Keys of the moneyline markets. `B` is null on a lone market slug, `draw` on an event without one. */
export interface MoneylineKeys {
  A: MarketKey;
  B: MarketKey | null;
  draw: MarketKey | null;
}

/** The Kalshi side of an arm. */
export interface KalshiInfo {
  eventTicker: string;
  /** The moneyline event plus its spread / total / both-teams-to-score siblings, where Kalshi lists them. */
  eventTickers: string[];
  /** How many armed markets Kalshi lists. */
  markets: number;
  contractsPerTrigger: number;
  /** How Kalshi's two team markets were paired with A/B. */
  matchedBy: 'names' | 'ticker-order';
  bookConnected: boolean;
}

/** Every armed market, keyed, in display order: moneyline first, then the rest of the fixture. */
export type Markets<T> = Record<MarketKey, MarketInfo<T>>;

export interface ArmResponse {
  armed: true;
  eventSlug: string;
  resolvedFrom: ResolvedFrom;
  /** Button labels — the two teams. */
  teams: Record<Side, string>;
  moneyline: MoneylineKeys;
  ignored: string[];
  /** Shares requested per leg per tap. A tap places one order of this size per leg it buys. */
  sharesPerTrigger: number;
  /** Most paid above the best bid, in dollars. The fallback when no model was fitted. */
  payUpBudget: number;
  /** Paper mode: fills are simulated against the live book and written to the DB; nothing is posted. */
  simulated: boolean;
  allowOutsideMatch?: boolean;
  autoSell?: AutoSellStatus;
  /** Goal rates fitted to the fixture's own moneyline, or null when it is not a 3-way market. */
  model: GoalModel | null;
  clock: ClockInfo;
  /** The scoreline at arm time, from the feed — non-zero when armed mid-match. */
  score: Record<Side, number>;
  firstScorer: Side | null;
  kalshi: KalshiInfo | null;
  markets: Markets<{ label: string; tokenId: string | null; rungs: number }>;
  taps: Record<Side, TapLeg[]>;
}

export interface FillRecord {
  at: number;
  side: Side;
  seq: number;
  venue: Venue;
  leg: LegKey;
  label: string;
  /** The limit posted. `avgPrice` is what was actually paid, and is lower on a real sweep. */
  price: number;
  shares: number;
  filledShares: number;
  avgPrice: number | null;
  orderId?: string;
  status?: string;
  latencyMs: number;
}

export interface SkipRecord {
  at: number;
  side: Side;
  seq: number;
  venue: Venue;
  leg: LegKey;
  label: string;
  reason: string;
  spread: number | null;
  ask: number | null;
}

export interface LegStatus {
  label: string;
  top: BookTop | null;
  filledShares: number;
}

/** One instrument the auto-sell is working — a Kalshi market or a Polymarket token: what is held, resting, sold. */
export interface AutoSellPosition {
  venue: Venue;
  /** Kalshi ticker, or Polymarket token id. */
  instrument: string;
  label: string;
  outcome: 'yes' | 'no' | null;
  held: number;
  resting: { price: number; count: number; postedAt: string } | null;
  sold: number;
  avgSold: number | null;
  /** The venue kept refusing our sells; left alone until the next tap. */
  abandoned: boolean;
  lastEvent: string;
}

export interface AutoSellStatus {
  on: boolean;
  /** The venues it is selling on: every venue the game trades on. */
  venues?: Venue[];
  /** Seconds the spread rule waits after a score change. */
  waitSeconds: number;
  positions: AutoSellPosition[];
}

export interface StatusResponse {
  armed: boolean;
  autoSell?: AutoSellStatus;
  eventSlug?: string;
  resolvedFrom?: ResolvedFrom;
  teams?: Record<Side, string>;
  moneyline?: MoneylineKeys;
  ignored?: string[];
  armedAt?: string;
  bookConnected?: boolean;
  sharesPerTrigger?: number;
  payUpBudget?: number;
  simulated?: boolean;
  model?: GoalModel | null;
  /** When the model was last fitted, and from what: the live book, or the listing when the book had no usable quote. */
  modelFittedAt?: string | null;
  modelSource?: 'book' | 'listing' | 'kalshi' | null;
  clock?: ClockInfo;
  score?: Record<Side, number>;
  firstScorer?: Side | null;
  kalshi?: KalshiInfo | null;
  markets?: Markets<LegStatus>;
  /** The legs each tap would buy at the current score. */
  taps?: Record<Side, TapLeg[]>;
  fills?: number;
  skips?: number;
  skipReasons?: Record<string, number>;
  /** Unmatched Yes-vs-No shares per market — the exposure the liquidator's merge cannot touch. */
  imbalance?: Partial<Record<MarketKey, number>>;
  imbalanceShares?: number;
  /** The same, on Kalshi. Null when no Kalshi event is armed. */
  kalshiImbalance?: Partial<Record<MarketKey, number>> | null;
  kalshiImbalanceShares?: number | null;
  latencyMs?: { min: number; median: number; max: number } | null;
  recentFills?: FillRecord[];
  recentSkips?: SkipRecord[];
}

/** One leg's decision, straight off the service's own gate chain. */
export interface LegPlan {
  venue: Venue;
  leg: LegKey;
  market: MarketKey;
  outcome: OutcomeKey;
  ticker: string | null;
  label: string;
  why: string;
  bid: number | null;
  ask: number | null;
  spread: number | null;
  fresh: boolean;
  askSize: number;
  fair: number | null;
  limit: number | null;
  reach: number | null;
  shares: number;
  maxCost: number | null;
  blockedBy: string | null;
}

export interface TapPlan {
  side: Side;
  team: string;
  /** "0–0 → 1–0": the scoreline this tap would move to. */
  scoreline: string;
  legs: LegPlan[];
}

export interface LegProjection {
  leg: LegKey;
  label: string;
  why: string;
  points: { minute: number; fair: number | null }[];
}

/** One market on the monitor: where it stands, and what each tap would do to it. */
export interface MarketOutlook {
  key: MarketKey;
  group: string;
  kind: string;
  label: string;
  outcomes: [string, string];
  legLabels: [string, string];
  /** Listed on Polymarket. */
  polymarket: boolean;
  kalshiTicker: string | null;
  /** Model probability of the first outcome now. Null without a model or clock. */
  fairNow: number | null;
  taps: Record<Side, { buys: OutcomeKey | null; fairAfter: number | null }>;
  /** Polymarket's book per leg; both null when Polymarket does not list the market. */
  book: { yes: BookTop | null; no: BookTop | null };
  held: { yes: number; no: number };
  /** Kalshi's book and position per leg, when Kalshi lists the market. */
  kalshiBook: { yes: BookTop | null; no: BookTop | null } | null;
  kalshiHeld: { yes: number; no: number } | null;
}

export interface PreviewResponse {
  armed: boolean;
  eventSlug?: string;
  teams?: Record<Side, string>;
  moneyline?: MoneylineKeys;
  minute?: number | null;
  firstScorer?: Side | null;
  ignored?: string[];
  /** Every armed market, in display order. */
  markets?: MarketOutlook[];
  clock?: ClockInfo;
  model?: GoalModel | null;
  /** When the model was last fitted, and from what: the live book, or the listing when the book had no usable quote. */
  modelFittedAt?: string | null;
  modelSource?: 'book' | 'listing' | 'kalshi' | null;
  score?: Record<Side, number>;
  sharesPerTrigger?: number;
  payUpBudget?: number;
  simulated?: boolean;
  bookConnected?: boolean;
  kalshi?: KalshiInfo | null;
  taps?: Record<Side, TapPlan>;
  projection?: { minutes: number[] } & Record<Side, LegProjection[]>;
}

/** What happened to one leg of a tap, on one venue. */
export interface LegOutcome {
  venue: Venue;
  leg: LegKey;
  label: string;
  why: string;
  fired: boolean;
  /** True when the fill was simulated in paper mode — nothing was posted. */
  simulated?: boolean;
  reason?: string;
  price?: number;
  shares?: number;
  filledShares?: number;
  avgPrice?: number | null;
  spread?: number | null;
  orderId?: string;
  status?: string;
  error?: string;
  latencyMs: number;
}

export interface TriggerOutcome {
  /** True when at least one leg placed an order. */
  fired: boolean;
  reason?: string;
  side: Side;
  /** Echoed back by the service so an outcome can be matched to the tap that caused it. */
  seq?: number;
  score?: Record<Side, number>;
  legs: LegOutcome[];
  latencyMs: number;
  gateMs?: number;
}

/**
 * The browser talks to the trader service DIRECTLY — proxying the trigger through backoffice-api
 * would add a network hop to the one path that must not have one. The service verifies the same
 * backoffice session JWT this SPA already holds, so there is no second credential anywhere.
 *
 * One service per sport, both running the same code: `sideline-trader` (soccer) and
 * `courtside-trader` (basketball). Host derivation mirrors `client.ts`: prod backoffice is served at
 * postrix.io, other namespaces at <ns>-backoffice.postrix.io.
 */
export type TraderService = 'sideline-trader' | 'courtside-trader';

const LOCAL_PORTS: Record<TraderService, number> = {
  'sideline-trader': 8475,
  'courtside-trader': 8474
};

export interface SidelineApiConfig {
  service: TraderService;
  /**
   * A local page can be pointed at the production service (`VITE_SIDELINE_URL=https://sideline-trader.postrix.io`);
   * its CORS policy admits localhost origins, and the token comes from whichever backoffice-api the dev proxy hits.
   */
  envUrl?: string;
}

function getTraderBaseUrl({ service, envUrl }: SidelineApiConfig): string {
  if (envUrl) {
    return envUrl.replace(/\/$/, '');
  }
  if (typeof window === 'undefined') {
    return '';
  }

  const { hostname, protocol } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://${hostname}:${LOCAL_PORTS[service]}`;
  }
  if (hostname === 'postrix.io') {
    return `${protocol}//${service}.postrix.io`;
  }
  if (hostname.includes('backoffice')) {
    return `${protocol}//${hostname.replace('backoffice', service)}`;
  }
  return `${protocol}//${hostname}`;
}

/** A point on a venue's public record, priced in the outcome we bought: a trade, or the venue's minute quote where nobody traded. */
export interface TapePoint {
  at: string;
  price: number;
  size: number;
  kind: 'trade' | 'quote';
}

/** One of our fills with the market around it — the post-match review. */
/** One order book as depth, best level first on each side. A size is null where the venue does not report one. */
export interface BookSnapshot {
  at: number;
  /** False where only the top of book is known (Kalshi's book is polled without depth). */
  depth: boolean;
  bids: [price: number, size: number | null][];
  asks: [price: number, size: number | null][];
}

/** What became of a leg: filled whole, filled in part, sent and matched nothing, or refused by a gate. */
export type ReviewLegStatus = 'filled' | 'partial' | 'missed' | 'skipped';

export interface ReviewLeg {
  venue: Venue;
  status: ReviewLegStatus;
  /** The gate that refused a skipped leg, or the venue's rejection; null on a leg that went through. */
  reason: string | null;
  orderId: string | null;
  market: string;
  outcome: string;
  /** What the service saw when it decided: the book, the model's value after the goal, and why it wanted the leg. Null for taps before it was recorded. */
  seen: {
    bid: number | null;
    ask: number | null;
    fair: number | null;
    why: string | null;
    /** The leg's order book as depth at the tap; null for taps made before it was saved. */
    book?: BookSnapshot | null;
    /** The same book about a second later — in play, the book a held order actually met. */
    bookAfter?: BookSnapshot | null;
  } | null;
  /** The instant the market is read around: when our order matched, or the tap itself where nothing matched. */
  at: string;
  /** When our order left the service; null when only the venue's record is available. */
  sentAt: string | null;
  /** When it matched — Polymarket to the second, Kalshi to the millisecond. Null when nothing matched. */
  boughtAt: string | null;
  /** From the service's own record: when the tap arrived, how long until this order left, how long until the venue replied. Null for taps before it was recorded. */
  timing: { tappedAt: string; tapToPostMs: number; postToReplyMs: number; phoneRttMs?: number | null } | null;
  /** The most the order was allowed to pay per share; null when the venue no longer has the order. */
  limit: number | null;
  /** Shares or contracts the order asked for; null when nothing recorded it. */
  requested: number | null;
  /** Size-weighted average actually paid per share; null when nothing matched. */
  paid: number | null;
  shares: number;
  cost: number;
  /** The last trade more than ten seconds before ours — the market before the goal. */
  before: TapePoint | null;
  /** The first trade by anyone else from ten seconds before ours on; before ours means we were beaten. */
  marketAfter: TapePoint | null;
  /** The last trade within a few minutes of ours. */
  after: TapePoint | null;
  /** Anyone else's trades in the ten seconds before ours. */
  beatenBy: TapePoint[];
  /** Off our own recording of the book around the tap; null for taps made before the recorder existed. */
  reaction: {
    tappedAt: string;
    /** The ask the tap hit, in the outcome's own price. */
    askAtTap: number | null;
    /** When the makers first repriced or pulled that ask, or null if they had not within the recorded window. */
    movedAt: string | null;
    askAfter: number | null;
  } | null;
  /** What the auto-sell sold of this leg, in the price of the outcome sold; null when nothing was. */
  exit?: { sold: number; avgPrice: number; proceeds: number; maker: number; taker: number; lastAt: string } | null;
  /** Every sell the auto-sell posted for the position this leg was the latest buy into, in posting order. */
  sells?: SellOrder[];
  /** What the venue charged on this leg: its buy fills' fees plus its share of the sells' that closed it. Null where not reported per fill (Polymarket). */
  fees?: number | null;
  result: 'won' | 'lost' | null;
  /** Realized and settled, before fees: the sold part's proceeds, plus the rest at settlement, less the cost. */
  pnl: number | null;
  /** (price three minutes on − paid) × size: what the lead was worth, whatever the match did. */
  lead: number | null;
}

/** One sell the auto-sell posted: the resting sell at the tap, a re-price, or the sale at the bid. */
export interface SellOrder {
  orderId: string;
  postedAt: string;
  /** From the tap to this order leaving, in milliseconds. */
  afterMs: number;
  kind: 'maker' | 'taker';
  /** `tap`, `feed 0-1 → 0-0`, `clock`, `spread`, `retry`. */
  reason: string;
  /** Its price, in the outcome sold: the resting sell's limit, or the bid the sale went into. */
  price: number;
  size: number;
  /** The model's value it was priced from; null on a sale at the bid. */
  fair: number | null;
  bid: number | null;
  ask: number | null;
  /** What it sold, from the venue's fills; zero for a resting sell cancelled before anyone took it. */
  filled: number;
  avgPrice: number | null;
}

export interface ReviewTap {
  at: string;
  legs: ReviewLeg[];
  pnl: number | null;
  lead: number | null;
}

export interface ReviewResponse {
  from: string;
  to: string;
  taps: ReviewTap[];
  pnl: { polymarket: number | null; kalshi: number | null };
}

/** The client for one trader service. Both sports share every endpoint and every JSON shape. */
export function createSidelineApi(config: SidelineApiConfig) {
  const baseUrl = getTraderBaseUrl(config);

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAuthToken();
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers as Record<string, string>)
      }
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body?.error || `${config.service} returned ${response.status}`);
    }
    return body as T;
  }

  return {
    service: config.service,

    baseUrl: () => baseUrl,

    arm: (params: {
      /** A Polymarket match URL or slug. May be left out with a `kalshiEvent`: the fixture is then Kalshi's alone. */
      marketUrl?: string;
      team?: string;
      sharesPerTrigger: number;
      payUpBudget?: number;
      /** Kalshi event ticker, market ticker or kalshi.com URL for the same fixture. */
      kalshiEvent?: string;
      kalshiContracts?: number;
      simulated?: boolean;
      /** Let real orders fire before kick-off and after full time. Off by default. */
      allowOutsideMatch?: boolean;
      /** Sell what the taps buy, on every venue the game trades on. Nothing is sold in paper mode. */
      autoSell?: boolean;
      /** Seconds the spread rule waits after a score change before it may sell at the bid. */
      autoSellWaitSeconds?: number;
    }) =>
      request<ArmResponse>('/arm', {
        method: 'POST',
        body: JSON.stringify(params)
      }),

    status: () => request<StatusResponse>('/status'),

    /** Everything bought between two instants, on both venues, against each market's public tape. */
    review: (from: string, to: string) =>
      request<ReviewResponse>(`/review?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

    /** Read-only dry run: what a tap would do, without doing it. */
    preview: () => request<PreviewResponse>('/preview'),

    // No merge here on purpose: the liquidator service already sweeps balanced YES+NO pairs back to
    // collateral every 5 minutes, wallet-wide.
    disarm: () => request<unknown>('/disarm', { method: 'POST', body: '{}' }),

    /** HTTP fallback used only when the trigger socket is down. */
    trigger: (side: Side, seq: number) =>
      request<TriggerOutcome>('/trigger', {
        method: 'POST',
        body: JSON.stringify({ side, seq })
      }),

    /**
     * The token is NOT in this URL. It is sent as the first frame instead — a query string would put
     * the backoffice session JWT into access logs, and that JWT opens the whole backoffice.
     */
    triggerSocketUrl: () => `${baseUrl.replace(/^http/, 'ws')}/trigger`,

    authFrame: () => JSON.stringify({ type: 'auth', token: getAuthToken() })
  };
}

export type SidelineApi = ReturnType<typeof createSidelineApi>;

/** Soccer: `sideline-trader`. */
export const sideline = createSidelineApi({
  service: 'sideline-trader',
  envUrl: import.meta.env.VITE_SIDELINE_URL as string | undefined
});

/** Basketball: `courtside-trader`, the same service code at its own host. */
export const courtside = createSidelineApi({
  service: 'courtside-trader',
  envUrl: import.meta.env.VITE_COURTSIDE_URL as string | undefined
});
